/**
 * Whether an instrument row is blocked: it cannot be held, nothing done on the row clears that, so
 * a table shows the block read-only and lets no bulk action reach the row. The issue cell
 * (`./issue-cell`) renders every block the same way.
 *
 * A row says it is blocked in two ways, which {@link rowBlock} parses into one value:
 * - an `issue` that keeps the instrument from being held — the issue is the block;
 * - `delisted: true` with no such issue — the platform knows the instrument cannot be held, but not
 *   why, so the block is the missing data, never a reason it was not given.
 */
import type { ReviewTickerIssueEnum } from "@eu.mdotm.api/sphere";

export type BlockingIssue = Extract<
	ReviewTickerIssueEnum,
	"DELISTED_INSTRUMENT" | "NON_SUPPORTED_CURRENCY" | "MATURED_BOND"
>;

export type RowBlock = { kind: "issue"; issue: BlockingIssue } | { kind: "missingData" };

const BLOCKING_ISSUES: ReadonlySet<ReviewTickerIssueEnum> = new Set<BlockingIssue>([
	"DELISTED_INSTRUMENT",
	"NON_SUPPORTED_CURRENCY",
	"MATURED_BOND",
]);

function isBlockingIssue(issue: ReviewTickerIssueEnum | undefined): issue is BlockingIssue {
	return issue != null && BLOCKING_ISSUES.has(issue);
}

type BlockableRow = { issue?: ReviewTickerIssueEnum; delisted?: boolean };

/** Whether anything blocks the row. */
export function isRowBlocked(row: BlockableRow): boolean {
	return rowBlock(row) != null;
}

/** What blocks the row, or `undefined` when nothing does. */
export function rowBlock(row: BlockableRow): RowBlock | undefined {
	if (isBlockingIssue(row.issue)) {
		return { kind: "issue", issue: row.issue };
	}
	if (row.delisted) {
		return { kind: "missingData" };
	}
	return undefined;
}
